/**
 * Cost Center Mapping Detail Page Component
 * /admin/cost-center-mappings/:id
 */

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Button,
  Paper,
  Grid,
  CircularProgress,
  Alert,
  Chip,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import apiClient from '../api/client';

const CostCenterMappingDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [mapping, setMapping] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    loadMapping();
  }, [id]);

  const loadMapping = async () => {
    try {
      setLoading(true);
      setError('');
      const data = await apiClient.getCostCenterMapping(id);
      setMapping(data);
    } catch (err) {
      setError(err.message || 'Failed to load cost center mapping details');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error && !mapping) {
    return (
      <Container maxWidth="lg">
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/cost-center-mappings')}>
          Back to Cost Center Mappings
        </Button>
      </Container>
    );
  }

  if (!mapping) {
    return (
      <Container maxWidth="lg">
        <Typography variant="h6">Cost center mapping not found</Typography>
        <Button startIcon={<ArrowBackIcon />} onClick={() => navigate('/admin/cost-center-mappings')}>
          Back to Cost Center Mappings
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/admin/cost-center-mappings')}
            sx={{ mb: 1 }}
          >
            Back to Cost Center Mappings
          </Button>
          <Typography variant="h4" component="h1">
            Cost Center Mapping Details
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Mapping ID: {mapping.id.substring(0, 8)}
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/admin/cost-center-mappings/${mapping.id}/edit`)}
        >
          Edit Mapping
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Basic Information */}
        <Grid size={{ xs: 12, md: 6 }}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Mapping Information
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Customer:
                </Typography>
                <Typography variant="body1">
                  {mapping.customer_name || mapping.customer_code || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Cost Center Code:
                </Typography>
                <Typography variant="h6" color="primary">
                  {mapping.cost_center_code}
                </Typography>
              </Box>
              {mapping.department && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Department:
                  </Typography>
                  <Typography variant="body1">{mapping.department}</Typography>
                </Box>
              )}
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status:
                </Typography>
                <Chip
                  label={mapping.is_active ? 'Active' : 'Inactive'}
                  color={mapping.is_active ? 'success' : 'default'}
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Description */}
        {mapping.description && (
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                Description
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Typography variant="body1">{mapping.description}</Typography>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Container>
  );
};

export default CostCenterMappingDetail;

